import { Card, Row, CardGroup, CardBody } from "reactstrap";
import chatIcon from "../../assets/images/chat.svg";
import checkIcon from "../../assets/images/check.svg";
import crossIcon from "../../assets/images/cross.svg";
import starIcon from "../../assets/images/star.svg";
import CountUp from "react-countup";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../../helpers/translateApiError";

// API service
import { fetchSmsLogsStatistics } from "../SmsLogs/api/smsLogsApi";
import InfoTip from "../../components/Common/InfoTip";

// Full number with thousands separators (55,230), same as the tenant details page.
// Values that are not numbers are shown as they are.
const handleNumberFormat = (num: any) => {
  const value = Number(num);
  if (num === null || num === undefined || num === "" || Number.isNaN(value))
    return num ?? "-";
  return value.toLocaleString("en-US");
};

const StatsCards = () => {
  const { t } = useTranslation();
  const {
    data: statistics,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["smsLogsStatistics"],
    queryFn: fetchSmsLogsStatistics,
    retry: 3,
  });

  // Show error state
  if (error && !loading) {
    return (
      <CardGroup className="stats-card-wrapper my-2 gap-3">
        <Card className="rounded">
          <CardBody>
            <div className="text-center text-danger p-3">
              <p>{t("dashboard.statsError.title")}</p>
              <small className="text-muted">
                {error instanceof Error
                  ? translateApiError(error.message)
                  : t("dashboard.statsError.unknown")}
              </small>
            </div>
          </CardBody>
        </Card>
      </CardGroup>
    );
  }

  return (
    <CardGroup className="stats-card-wrapper my-2 gap-3">
      {/* ALL SMS */}
      <Card className="rounded">
        <CardBody>
          <Row>
            {/* TODO try using duration prop of skeleton instead of settimeout in fetch */}
            <div className="d-flex flex-wrap gap-3">
              <div className="flex-1">
                {loading ? (
                  <Skeleton count={0.5} />
                ) : (
                  <p>{t("dashboard.cards.allSms")}</p>
                )}
              </div>
              {loading ? (
                <Skeleton circle height={48} width={48} />
              ) : (
                <div className="stats-card-img-wrapper bg-primary">
                  <img src={chatIcon} alt="" />
                </div>
              )}
            </div>
          </Row>
          <h3>
            {loading ? (
              <Skeleton count={0.3} />
            ) : (
              <CountUpMemoized endCount={statistics.ALL} />
            )}
          </h3>
        </CardBody>
      </Card>
      {/* SUCCESSFUL SMS */}
      <Card className="rounded">
        <CardBody>
          <Row>
            <div className="d-flex">
              <div className="flex-1">
                {loading ? (
                  <Skeleton count={0.5} />
                ) : (
                  <p>{t("dashboard.cards.successfulSms")}</p>
                )}
              </div>
              {loading ? (
                <Skeleton circle height={48} width={48} />
              ) : (
                <div className="stats-card-img-wrapper bg-success">
                  <img src={checkIcon} alt="" />
                </div>
              )}
            </div>
          </Row>
          <h3>
            {loading ? (
              <Skeleton count={0.3} />
            ) : (
              <CountUpMemoized endCount={statistics.SUCCESS} />
            )}
          </h3>
        </CardBody>
      </Card>
      {/* FAILED SMS */}
      <Card className="rounded">
        <CardBody>
          <Row>
            <div className="d-flex">
              <div className="flex-1">
                {loading ? (
                  <Skeleton count={0.5} />
                ) : (
                  <p>{t("dashboard.cards.failedSms")}</p>
                )}
              </div>
              {loading ? (
                <Skeleton circle height={48} width={48} />
              ) : (
                <div className="stats-card-img-wrapper bg-danger">
                  <img src={crossIcon} alt="" />
                </div>
              )}
            </div>
          </Row>
          <h3>
            {loading ? (
              <Skeleton count={0.3} />
            ) : (
              <CountUpMemoized endCount={statistics.FAILED} />
            )}
          </h3>
        </CardBody>
      </Card>
      {/* REMAINING PTS */}
      <Card className="rounded">
        <CardBody>
          <Row>
            <div className="d-flex">
              <div className="flex-1">
                {loading ? (
                  <Skeleton count={0.5} />
                ) : (
                  <p>
                    {t("dashboard.cards.remainingPoints")}
                    <InfoTip text={t("dashboard.cards.remainingPointsHint")} />
                  </p>
                )}
              </div>
              {loading ? (
                <Skeleton circle height={48} width={48} />
              ) : (
                <div className="stats-card-img-wrapper bg-info">
                  <img src={starIcon} alt="" />
                </div>
              )}
            </div>
          </Row>
          <h3>
            {loading ? (
              <Skeleton count={0.3} />
            ) : (
              <CountUpMemoized endCount={statistics.REMAINING_POINTS} />
            )}
          </h3>
        </CardBody>
      </Card>
    </CardGroup>
  );
};

export default StatsCards;

const CountUpMemoized = memo(function CountUpMemoized({ endCount }: any) {
  return (
    <CountUp
      start={0}
      end={endCount}
      decimals={0}
      duration={3}
      formattingFn={(val: any) => handleNumberFormat(val)}
    />
  );
});
